import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { PenLine } from 'lucide-react';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';
import { RevealPhotos } from './RevealPhotos';
import { useRevealViewport, PreparingIndicator } from '../useRevealViewport';
import type { FullCanvasSceneProps } from './types';

import { DepthRevealLayer } from '../cinematic/DepthRevealLayer';
const EASE = [0.22, 0.61, 0.36, 1] as const;

export function FullCanvasHandwritten({ recipientName, relationship, message, photoUrls, onComplete, startAtMessage = false }: FullCanvasSceneProps) {
  const [phase, setPhase] = useState<'darkness' | 'cover' | 'opening' | 'writing' | 'done'>(startAtMessage ? 'writing' : 'darkness');
  const [charIndex, setCharIndex] = useState(0);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const delayRef = useRef<number>(0);
  const doneRef = useRef(false);
  const indexRef = useRef(0);
  const { isMobile, burstOriginY, timingMultiplier: tm } = useRevealViewport();

  const t = (ms: number) => ms * tm;

  useEffect(() => {
    if (startAtMessage) return;
    const t1 = setTimeout(() => setPhase('cover'), t(4000));
    const t2 = setTimeout(() => setPhase('opening'), t(5500));
    const t3 = setTimeout(() => setPhase('writing'), t(7500));
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [startAtMessage, tm]);

  useEffect(() => {
    if (phase !== 'writing') return;

    indexRef.current = 0;
    doneRef.current = false;
    lastTimeRef.current = 0;
    delayRef.current = startAtMessage ? 1200 : 800;

    const getCharDelay = (char: string): number => {
      const base = 35 + (Math.random() - 0.4) * 10;
      if (char === '.' || char === '!' || char === '?') return base + 280;
      if (char === ',') return base + 150;
      if (char === '\n') return base + 350;
      if (char === ';' || char === ':') return base + 120;
      return base;
    };

    const tick = (timestamp: number) => {
      if (doneRef.current) return;
      if (lastTimeRef.current === 0) lastTimeRef.current = timestamp;
      const elapsed = timestamp - lastTimeRef.current;
      if (elapsed >= delayRef.current) {
        lastTimeRef.current = timestamp;
        indexRef.current++;
        setCharIndex(indexRef.current);
        if (indexRef.current >= message.length) {
          doneRef.current = true;
          setTimeout(() => { setPhase('done'); onComplete(); }, 350);
          return;
        }
        delayRef.current = getCharDelay(message[indexRef.current - 1]);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, message, onComplete, startAtMessage]);

  const isOpen = phase === 'writing' || phase === 'done';
  const showCover = phase === 'cover' || phase === 'opening';

  return (
    <div className={`min-h-[100dvh] sm:min-h-screen relative ${isOpen ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'}`}>
      <motion.div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #1a1206 0%, #2a1f0e 50%, #1a1206 100%)' }}
        initial={{ opacity: 1 }}
        animate={{ opacity: isOpen ? 0 : 1 }}
        transition={{ duration: 1.5, ease: EASE as any }}
      />

      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #f5ecd7 0%, #f0e4c8 50%, #ede0b8 100%)' }} />

      {/* Mobile preparing indicator */}
      {isMobile && <PreparingIndicator visible={phase === 'darkness'} color="rgba(217,159,53,0.7)" />}

      {phase === 'darkness' && (
        <>
          <motion.div
            className="absolute pointer-events-none"
            style={{
              left: '50%', top: `${burstOriginY}%`, width: 200, height: 200,
              marginLeft: -100, marginTop: -100, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(217,159,53,0.12) 0%, rgba(180,120,30,0.04) 40%, transparent 70%)',
            }}
            animate={{ opacity: [0.3, 1, 0.5, 0.9, 0.4], scale: [0.9, 1.1, 0.95, 1.05, 0.9] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          {[0, 1, 2].map(i => (
            <motion.div
              key={`ember-${i}`}
              className="absolute rounded-full pointer-events-none"
              style={{
                width: isMobile ? 4 : 2, height: isMobile ? 4 : 2,
                left: `${48 + i * 2}%`, top: `${(burstOriginY - 2) + i * 3}%`,
                background: 'rgba(251,191,36,0.8)',
                boxShadow: '0 0 8px 3px rgba(251,191,36,0.4)',
              }}
              animate={{ opacity: [0, 0.8, 0], y: [0, -12, -20] }}
              transition={{ duration: 1.5, delay: i * 0.6, repeat: Infinity, ease: 'easeOut' }}
            />
          ))}
        </>
      )}

      {showCover && (
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(145deg, #3d2b1f 0%, #5c3d2e 30%, #4a332a 60%, #2d1e14 100%)',
            transformOrigin: 'left center', zIndex: 30,
          }}
          initial={{ opacity: phase === 'cover' ? 0 : 1, rotateY: phase === 'opening' ? 0 : 0 }}
          animate={phase === 'cover' ? { opacity: 1, rotateY: 0 } : { rotateY: -180 }}
          transition={phase === 'cover' ? { duration: 0.8, ease: EASE as any } : { duration: 2.0, ease: [0.4, 0, 0.2, 1] }}
        >
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <motion.div className="text-center" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}>
              <div className="w-24 h-px mx-auto mb-4" style={{ background: 'rgba(217,159,53,0.4)' }} />
              <p className="font-serif text-2xl md:text-3xl tracking-wider" style={{ color: 'rgba(217,159,53,0.7)' }}>A Message</p>
              <p className="text-xs tracking-[0.4em] uppercase mt-2" style={{ color: 'rgba(217,159,53,0.5)' }}>For {recipientName}</p>
              <div className="w-24 h-px mx-auto mt-4" style={{ background: 'rgba(217,159,53,0.4)' }} />
            </motion.div>
          </div>
          <div className="absolute left-0 top-0 bottom-0 w-8" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.4), transparent)' }} />
        </motion.div>
      )}

      {phase === 'opening' && (
        <motion.div className="absolute inset-0" style={{ zIndex: 25, background: '#fefbf3' }}
          initial={{ opacity: 0 }} animate={{ opacity: [0, 0.6, 0] }} transition={{ duration: 0.8, delay: 1.2 }}
        />
      )}

      <motion.div className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: isOpen ? 1 : 0 }} transition={{ duration: 0.5 }}>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom right, #fffbf0, rgba(255,237,213,0.8), #fefce8)' }} />
        <div className="absolute inset-0 opacity-[0.12]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(180,120,60,0.08) 60%, rgba(120,80,40,0.15) 100%)' }} />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'repeating-linear-gradient(180deg, transparent, transparent 32px, rgba(139,90,43,0.3) 32px, rgba(139,90,43,0.3) 33px)',
          backgroundPositionY: '180px',
        }} />
        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 80px 20px rgba(120,80,40,0.06)' }} />
      </motion.div>

      {Array.from({ length: 10 }).map((_, i) => (
        <motion.div
          key={`dust-${i}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 2 + (i % 3), height: 2 + (i % 3),
            left: `${10 + i * 9}%`, top: `${15 + (i * 14) % 65}%`,
            background: `radial-gradient(circle, rgba(180,150,100,${0.2 + (i % 3) * 0.1}), transparent)`,
          }}
          animate={{ y: [0, -(10 + i * 4), 0], opacity: [0, 0.5, 0] }}
          transition={{ duration: 4 + i * 0.6, delay: t(7000) / 1000 + i * 0.8, repeat: Infinity, ease: EASE as any }}
        />
      ))}

      {isOpen && (
        <motion.div className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: startAtMessage ? 1.8 : 0.8 }}>
          <div className="max-w-2xl mx-auto">
            <motion.div className="text-center mb-8" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3, ease: EASE as any }}>
              <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: 'rgba(146,103,20,0.5)' }}>A message for</p>
              <h1 className="font-serif text-3xl md:text-4xl tracking-wide" style={{ color: '#78350f' }}>{recipientName}</h1>
              <div className="flex items-center justify-center gap-3 mt-4">
                <div className="w-12 h-px" style={{ background: 'rgba(146,103,20,0.2)' }} />
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(180,120,30,0.3)' }} />
                <div className="w-12 h-px" style={{ background: 'rgba(146,103,20,0.2)' }} />
              </div>
            </motion.div>

            <RevealPhotos photoUrls={photoUrls} visible={isOpen} delay={0.6} borderColor="rgba(217,159,53,0.5)" shadowColor="rgba(120,80,40,0.2)" />

            <div className="relative min-h-[300px]">
              <p className="font-serif text-xl md:text-2xl leading-[2] whitespace-pre-wrap" style={{ color: 'rgba(120,53,15,0.9)' }}>
                {message.slice(0, charIndex)}
                {charIndex > 0 && ['.', '!', '?'].includes(message[charIndex - 1]) && (
                  <motion.span className="inline-block w-1 h-1 rounded-full ml-0.5 align-middle" style={{ background: 'rgba(146,103,20,0.3)' }}
                    initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 3, 0], opacity: [0, 0.4, 0] }} transition={{ duration: 0.8, ease: 'easeOut' }}
                  />
                )}
                {charIndex < message.length && (
                  <motion.span animate={{ y: [0, -2, 0], rotate: [0, -8, 0] }} transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut' }}
                    className="inline-flex items-center ml-0.5 align-middle" style={{ color: 'rgba(146,103,20,0.7)' }}
                  >
                    <PenLine className="h-5 w-5" />
                  </motion.span>
                )}
              </p>
            </div>

            {charIndex >= message.length && (
              <motion.div className="mt-12 text-center" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3, ease: EASE as any }}>
                <p className="text-lg font-serif italic" style={{ color: 'rgba(146,83,20,0.8)' }}>
                  — From your {getSenderPerspectiveLabel(relationship)}
                </p>
                <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(217,159,53,0.3)' }}>
                  <p className="text-xs tracking-wider" style={{ color: 'rgba(180,120,30,0.4)' }}>Say It NOW • While They're Still Here</p>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}

      <motion.div className="absolute inset-0 pointer-events-none"
        animate={{ x: [0, 1, -1, 0], y: [0, -0.5, 0.5, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <DepthRevealLayer active={isOpen} tint="rgba(220,180,120,0.5)" glow="rgba(245,222,179,0.5)" />
    </div>
  );
}
